/**
 * The device editor's variant pickers, as data.
 *
 * A device's "kind" used to be written into `device.icon` by six bespoke rows in
 * `DeviceEditor.vue` — six option tables, six `is…Device` predicates and six
 * `apply…Type` writers, each a copy of the others. The hub now declares the kind
 * as a *setting* (`pump_type`, `tank_type`, `cover_style`, …) with
 * `display: 'drawing'`, so the editor renders whatever the class declares and the
 * icon goes back to being the device's glyph.
 *
 * This module is the UI half of that contract: for each `drawing` family it
 * carries the bilingual wording the hub's English labels cannot, the default, and
 * the resolver that reads a device (the setting first, then the legacy icon and
 * name the pre-setting devices were saved with).
 *
 * No Vue, no store: pure data and pure functions, so the Node test runner can
 * exercise it.
 */
export { readSetting, variantFromSetting } from '../../../utils/drawingSetting';

/** A drawing family's wording, `[English, Slovene]` like the rest of the app. */
export type Bilingual = [string, string];

/** One choice of a variant setting, as the editor renders it. */
export interface VariantOption {
  /** The value stored in `settings.<settingKey>` — and the drawing's own id. */
  value: string;
  /** The Material Symbol the option shows. */
  icon: string;
  label: Bilingual;
  /** A socket's Connected Load Name (`settings.device_name`), where the row had one. */
  load?: string;
}

/**
 * One drawing family. `drawing` is the id the hub's setting carries
 * (`drawing: 'pump'`); `settingKey` is the setting it is read from.
 */
export interface VariantFamily {
  drawing: string;
  settingKey: string;
  /** The picker's own wording, used when the class metadata carries none. */
  title: Bilingual;
  description: Bilingual;
  options: VariantOption[];
  /** The value a device gets when nothing has been picked. */
  defaultValue: string;
  /**
   * The device's kind: the setting's value when the device carries one, then the
   * legacy icon/name fallback the widget resolvers already implement.
   */
  resolve(device: VariantDevice): string;
}

/** What a resolver may read off a device. */
export interface VariantDevice {
  /** `device.settings` — where the kind lives now. */
  settings?: unknown;
  icon?: string | null;
  deviceName?: string | null;
  shortName?: string | null;
  deviceType?: string | null;
  controlType?: string | null;
  deviceControl?: string | null;
  unit?: string | null;
  valueNames?: (string | null | undefined)[];
  /** `settings.device_name` — a socket's Connected Load Name. */
  connectedLoadName?: string | null;
  /** `settings.binary_sensor_subtype` (its own setting before this work). */
  subtype?: string | null;
}

/** The values a family can resolve to — its options' order, less `auto`. */
export function variantValues(family: Pick<VariantFamily, 'options'>): string[] {
  return family.options.map((option) => option.value).filter((value) => value !== 'auto');
}
